import React, { useState } from "react";

import SingleItemSelection from "sulu-admin-bundle/components/SingleItemSelection";
import Overlay from "sulu-admin-bundle/components/Overlay/Overlay.js";
import { Table, Loader } from "sulu-admin-bundle/components";
import Pagination from "sulu-admin-bundle/components/Pagination";

import styles from "./player-select.module.scss";
import DynamicSearch from "./DynamicSearch";
import { useEffect } from "react";
import { parametarExtractor } from "../../utils/parametarExtractor";

import { ResourceRequester } from "sulu-admin-bundle/services";
import { useTranslation } from "react-i18next";

const Header = Table.Header;
const Body = Table.Body;
const Row = Table.Row;
const Cell = Table.Cell;
const HeaderCell = Table.HeaderCell;

const PlayerSearch = (props) => {
    const { t } = useTranslation();
    const [state, setState] = useState({
        open: false,
        isLoading: false,
        paginationChanged: false,
        paginationOptions: {
            limit: 10,
            page: 1,
            pages: null,
        },
        selectedPlayer: null,
        wasPlayerSelected: false,
        rows: [],
        noItems: false,
    });

    const {
        open,
        isLoading,
        paginationChanged,
        paginationOptions,
        selectedPlayer,
        rows,
        noItems,
    } = state;

    const openModalHandler = () => {
        setState((prev) => ({
            ...prev,
            open: true,
            ...(props.value ? { rows: [selectedPlayer] } : {}),
        }));
    };

    const closeHandler = () => {
        setState((prev) => ({
            ...prev,
            open: false,
            noItems: false,
            selectedPlayer: props.value
                ? prev.selectedPlayer
                : prev.selectedPlayer
                ? null
                : prev.selectedPlayer,
        }));
    };

    const confirmHandler = () => {
        if (selectedPlayer) {
            props.onChange(selectedPlayer.id);
        }
        setState((prev) => ({
            ...prev,
            noItems: false,
            ...(selectedPlayer ? { selectedPlayer: selectedPlayer } : {}),
            open: false,
        }));
    };

    function isSelected(playerId) {
        return playerId === selectedPlayer?.id;
    }

    const resourceKey = parametarExtractor(props)?.resourceKey ?? "contacts";

    const handleRowSelectionChange = (playerId) => {
        const newObject = {};
        for (const obj of rows) {
            newObject[obj.id] = obj;
        }
        setState((prev) => ({
            ...prev,
            selectedPlayer: newObject[playerId],
        }));
    };

    const fetch = async (paginationOptions, params) => {
        const queryParams = {
            locale: "de",
            flat: true,
            limit: paginationOptions.limit,
            page: paginationOptions.page,
            ...params,
        };

        await ResourceRequester.get(resourceKey, queryParams)
            .then((data) => {
                if (data._embedded.length === 0) {
                    setState((prev) => ({
                        ...prev,
                        noItems: true,
                    }));
                    return;
                }
                setState((prev) => ({
                    ...prev,
                    rows: data._embedded[resourceKey],
                    paginationOptions: {
                        ...prev.paginationOptions,
                        pages: data.pages,
                    },
                    noItems: false,
                    isLoading: false,
                }));
            })
            .catch((error) => {
                console.log(error);
                setState((prev) => ({
                    ...prev,
                    isLoading: false,
                }));
            });
    };

    useEffect(() => {
        if (rows.length) {
            fetch(paginationOptions);
        }
    }, [paginationChanged]);

    let body = null;

    if (isLoading) {
        body = (
            <Row key="loading">
                <Cell colSpan={3}>
                    <Loader />
                </Cell>
            </Row>
        );
    } else {
        body = rows.map((player, index) => {
            let formattedDate = "/";

            if (player.birthday) {
                const birthday = new Date(player.birthday);
                const day = birthday.getDate();
                const month = birthday.getMonth() + 1;
                const year = birthday.getFullYear();

                formattedDate = `${day < 10 ? "0" + day : day}.${
                    month < 10 ? "0" + month : month
                }.${year}`;
            }

            return (
                <Row
                    key={index}
                    id={player.id}
                    selected={isSelected(player.id)}
                >
                    <Cell>{player.firstName}</Cell>
                    <Cell>{player.lastName}</Cell>
                    <Cell>{formattedDate}</Cell>
                </Row>
            );
        });
    }

    return (
        <div>
            <SingleItemSelection
                id={selectedPlayer && selectedPlayer?.id}
                emptyText={t("sulu_admin.passports.selected_player")}
                leftButton={{ icon: "su-user", onClick: openModalHandler }}
                onRemove={() => {
                    if (props.value) {
                        props.onChange(null);
                        setState((prev) => ({
                            ...prev,
                            selectedPlayer: null,
                            rows: [],
                        }));
                    }
                }}
            >
                {selectedPlayer &&
                    `${selectedPlayer.firstName} ${selectedPlayer.lastName}`}
            </SingleItemSelection>
            <Overlay
                confirmDisabled={selectedPlayer ? false : true}
                open={open}
                onClose={closeHandler}
                onConfirm={confirmHandler}
                title={t("sulu_admin.passports.search_player")}
                footer={false}
                confirmText={t("sulu_admin.passports.confirm")}
            >
                <div className={styles["player-select__overlay"]}>
                    <div className={styles["player-select__search-container"]}>
                        <DynamicSearch
                            data={props}
                            onSearch={fetch}
                            paginationOptions={paginationOptions}
                            translate={t}
                        />
                    </div>

                    <Table
                        selectMode="multiple"
                        onRowSelectionChange={handleRowSelectionChange}
                        placeholderText={
                            noItems ? t("sulu_admin.passports.no_players") : ""
                        }
                    >
                        <Header>
                            <HeaderCell>
                                {t("sulu_admin.users.firstname")}
                            </HeaderCell>
                            <HeaderCell>
                                {t("sulu_admin.users.lastname")}
                            </HeaderCell>
                            <HeaderCell>{t("dhb.contact.dob")}</HeaderCell>
                        </Header>
                        <Body>{body}</Body>
                    </Table>

                    <Pagination
                        currentLimit={paginationOptions?.limit}
                        currentPage={paginationOptions?.page}
                        onLimitChange={(limit) => {
                            if (rows.length) {
                                setState((prev) => ({
                                    ...prev,
                                    isLoading: true,
                                    paginationOptions: {
                                        ...paginationOptions,
                                        limit: limit,
                                    },
                                    paginationChanged: !paginationChanged,
                                }));
                            }
                        }}
                        onPageChange={(page) => {
                            setState((prev) => ({
                                ...prev,
                                isLoading: true,
                                paginationOptions: {
                                    ...paginationOptions,
                                    page: page,
                                },
                                paginationChanged: !paginationChanged,
                            }));
                        }}
                        totalPages={paginationOptions?.pages}
                    />
                </div>
            </Overlay>
        </div>
    );
};

export default PlayerSearch;
